// src/pages/PreviewPage.tsx
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Sparkles,
  Heart,
  Shield,
  Music,
} from "lucide-react";
import { useCard } from "../hooks/useCard";
import { RecipientFlow } from "../components/recipient/RecipientFlow";
import { ARCHETYPES, ArchetypeId } from "../constants/archetypes";
import logo from "../assets/img/logo.png";

export default function PreviewPage() {
  const { cardId } = useParams<{ cardId: string }>();
  const navigate = useNavigate();
  const { card, loading } = useCard(cardId);
  const [showStartPrompt, setShowStartPrompt] = useState(true);
  const [showCTABanner, setShowCTABanner] = useState(false);

  useEffect(() => {
    if (card && !showStartPrompt) {
      const timer = setTimeout(() => setShowCTABanner(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [card, showStartPrompt]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleEdit = () => {
    navigate(`/create/${cardId}`);
  };

  const handleStartExperience = () => {
    setShowStartPrompt(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full"
        />
      </div>
    );
  }

  if (!card) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Preview Not Available
          </h2>
          <button
            onClick={() => navigate("/dashboard")}
            className="px-6 py-3 bg-primary text-white rounded-xl"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const archetypeId = (card.archetype || "heartbeat") as ArchetypeId;
  const archConfig = ARCHETYPES[archetypeId];

  // Use the actual card colors for theming
  const primaryColor =
    card.primaryColor || archConfig?.defaultCardData.primaryColor || "#E11D48";
  const secondaryColor =
    card.secondaryColor ||
    archConfig?.defaultCardData.secondaryColor ||
    "#000000";

  return (
    <div className="relative w-full h-screen overflow-hidden bg-black">
      {/* Pass shouldAutoAdvance={false} to prevent auto-advancing */}
      <RecipientFlow
        card={card}
        isPreview={true}
        nextIsPay={true}
        shouldAutoAdvance={false}
      />

      {/* Preview Controls Header */}
      <div
        className="fixed top-0 left-0 right-0 py-4 px-2 flex justify-between items-start pointer-events-none"
        style={{ zIndex: 9999 }}
      >
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={handleBack}
          className="pointer-events-auto group flex items-center gap-3 bg-white/5 hover:bg-white/10 backdrop-blur-2xl text-white border border-white/10 px-5 py-3 rounded-full transition-all active:scale-95"
        >
          <ArrowLeft
            size={18}
            className="group-hover:-translate-x-1 transition-transform"
          />
          <span className="text-[10px] font-black uppercase tracking-[0.2em]">
            Back
          </span>
        </motion.button>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="pointer-events-auto flex items-center gap-4 bg-white/5 backdrop-blur-2xl border border-white/10 px-5 py-3 rounded-full"
        >
          <div className="flex items-center gap-2">
            {archetypeId === "moments" && (
              <Sparkles size={14} className="text-yellow-500" />
            )}
            {archetypeId === "heartbeat" && (
              <Heart size={14} className="text-pink-500" />
            )}
            {archetypeId === "safehaven" && (
              <Shield size={14} className="text-blue-500" />
            )}
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/70">
              Previewing Your Creation
            </span>
          </div>
        </motion.div>

        {card?.status === "draft" && card.isEditable && (
          <motion.button
            onClick={handleEdit}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="pointer-events-auto font-black uppercase text-black flex items-center gap-4 bg-white backdrop-blur-2xl border border-black/10 px-5 py-3 rounded-full"
          >
            <span className="text-xs">Edit Design</span>
            <ArrowRight size={18} />
          </motion.button>
        )}
      </div>

      {/* Start Prompt - Using actual card colors */}
      <AnimatePresence>
        {showStartPrompt && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 1.1, filter: "blur(20px)" }}
            transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[10000] flex flex-col items-center justify-center overflow-hidden"
            style={{
              background: `radial-gradient(circle at center, #000 0%, ${secondaryColor} 100%)`,
            }}
          >
            {/* Cinematic Ambience - Using card's primary color */}
            <motion.div
              animate={{
                opacity: [0.2, 0.4, 0.2],
                scale: [1, 1.2, 1],
              }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
              className="absolute inset-0 pointer-events-none"
              style={{
                background: `radial-gradient(circle at center, ${primaryColor}15 0%, transparent 70%)`,
              }}
            />

            {/* Atmospheric Particles */}
            <div className="absolute inset-0 pointer-events-none">
              {[...Array(20)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute rounded-full bg-white"
                  style={{
                    width: Math.random() * 2 + 1 + "px",
                    height: Math.random() * 2 + 1 + "px",
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    opacity: 0,
                  }}
                  animate={{
                    y: [0, -100],
                    opacity: [0, 0.3, 0],
                  }}
                  transition={{
                    duration: 10 + Math.random() * 10,
                    repeat: Infinity,
                    delay: Math.random() * 5,
                    ease: "linear",
                  }}
                />
              ))}
            </div>

            {/* Centerpiece */}
            <div className="relative z-10 flex flex-col items-center max-w-sm px-8">
              {/* Logo with Halo - Using card's primary color */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.5, ease: "easeOut" }}
                className="mb-12 relative flex items-center justify-center"
              >
                <motion.div
                  animate={{
                    y: [0, -8, 0],
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="relative z-10"
                >
                  <img
                    src={logo}
                    alt="LRNSY Logo"
                    className="w-20 h-20 md:w-24 md:h-24 object-contain brightness-110"
                  />
                </motion.div>

                {/* Ambient Halo - Using card's primary color */}
                <motion.div
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.5, 0.3],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="absolute inset-0 blur-3xl rounded-full scale-150"
                  style={{
                    background: `radial-gradient(circle, ${primaryColor}44 0%, transparent 70%)`,
                  }}
                />
              </motion.div>

              {/* Title Section */}
              <div className="text-center space-y-4 mb-16">
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.4 }}
                  transition={{ delay: 0.5, duration: 1 }}
                  className="text-[10px] uppercase tracking-[0.5em] text-white"
                >
                  You created
                </motion.p>

                <motion.h2
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 1.2 }}
                  className="text-4xl md:text-5xl font-serif italic text-white font-light"
                >
                  For {card.recipientName}
                </motion.h2>

                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: "40px" }}
                  transition={{ delay: 1.2, duration: 1.5 }}
                  className="h-px bg-white/20 mx-auto mt-6"
                />
              </div>

              {/* Begin Button */}
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.8, duration: 1 }}
                onClick={handleStartExperience}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="group relative flex flex-col items-center gap-4"
              >
                <div
                  className="px-12 py-5 rounded-full border border-white/10 bg-white/5 backdrop-blur-md text-white transition-all group-hover:bg-white group-hover:text-black group-hover:border-transparent overflow-hidden"
                  style={{
                    boxShadow: `0 10px 30px -5px ${primaryColor}40`,
                  }}
                >
                  <div className="relative z-10 flex items-center gap-3">
                    <span className="text-xs uppercase tracking-[0.3em] font-medium">
                      Preview Your Moment
                    </span>
                    <ArrowRight
                      size={14}
                      className="group-hover:translate-x-1 transition-transform"
                    />
                  </div>
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:animate-shimmer"
                    initial={false}
                  />
                </div>

                <div className="flex items-center gap-2 opacity-30 group-hover:opacity-60 transition-opacity">
                  <Music size={10} />
                  <span className="text-[9px] uppercase tracking-widest font-light">
                    Sound Recommended
                  </span>
                </div>
              </motion.button>
            </div>

            {/* Footer Signature */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.2 }}
              transition={{ delay: 2.5 }}
              className="absolute bottom-12"
            >
              <span className="text-[10px] tracking-[0.8em] text-white uppercase font-light">
                LRNSY
              </span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA Banner for when they want to proceed to payment */}
      <AnimatePresence>
        {showCTABanner && !showStartPrompt && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-6 left-0 right-0 z-[10001] px-4 pointer-events-auto"
          >
            <button
              onClick={() => navigate(`/payment/${cardId}`)}
              className="max-w-md mx-auto w-full flex items-center justify-between text-white px-8 py-5 rounded-2xl font-bold shadow-2xl cursor-pointer hover:scale-[1.02] transition-transform"
              style={{
                background: `linear-gradient(135deg, black, ${primaryColor})`,
              }}
            >
              <div className="flex items-center gap-4">
                {/* LOGO REPLACEMENT FOR SPARKLES */}
                <div className="w-8 h-8 rounded-lg bg-white/10 p-1.5 backdrop-blur-md">
                  <img
                    src={logo}
                    alt=""
                    className="w-full h-full object-contain brightness-200"
                  />
                </div>
                <div className="flex flex-col items-start">
                  <span className="text-[13px] tracking-tight">
                    Ready to Send?
                  </span>
                  <span className="text-[9px] uppercase tracking-[0.2em] opacity-50 font-black">
                    Complete Purchase
                  </span>
                </div>
              </div>
              <div className="bg-white/10 p-2 rounded-full">
                <ArrowRight size={20} />
              </div>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
